import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import { Globals } from "../globals";
import { useAuth } from "../hooks/useAuth";
import { useGemeinde } from "../context/GemeindeContext";
import I_IMC_Props from "../infos/I_IMC_Props";
import Table from "react-bootstrap/esm/Table";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Card from "react-bootstrap/esm/Card";
import Spinner from "react-bootstrap/esm/Spinner";
import Modal from "react-bootstrap/esm/Modal";

interface Member {
    uid: string;
    email: string;
    displayName: string;
    role: string | null;
    lastSignIn: string | null;
    disabled: boolean;
}

type Role = typeof Globals.ROLE_MEMBER | typeof Globals.ROLE_GEMEINDEADMIN;

/**
 * The accounts of the active Gemeinde (GOD-40): invite, change the role,
 * remove. The server limits all of it to the own Gemeinde and refuses changes
 * to one's own account.
 */
export default function GemeindeMembers({ setVariant, setMessage, setShowMessage }: I_IMC_Props) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { activeGemeindeId } = useGemeinde();
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRole, setInviteRole] = useState<Role>(Globals.ROLE_MEMBER);
    const [toRemove, setToRemove] = useState<Member | null>(null);

    const notify = useCallback(
        (variant: string, message: string) => {
            setVariant(variant);
            setMessage(message);
            setShowMessage(true);
        },
        [setVariant, setMessage, setShowMessage]
    );

    // Reload only when the Gemeinde changes, not whenever notify/t get new identities.
    const notifyRef = useRef(notify);
    notifyRef.current = notify;
    const load = useCallback(async () => {
        if (!activeGemeindeId) return;
        setLoading(true);
        try {
            const list = httpsCallable<{ gemeindeId: string }, { members: Member[] }>(functions, "listGemeindeMembers");
            setMembers((await list({ gemeindeId: activeGemeindeId })).data.members);
        } catch (err: any) {
            notifyRef.current("danger", err?.message ?? "");
        }
        setLoading(false);
    }, [activeGemeindeId]);

    useEffect(() => {
        load();
    }, [load]);

    async function assign(email: string, role: Role, resendInvitation = false) {
        if (!activeGemeindeId) return null;
        const call = httpsCallable<object, { created: boolean; invited: boolean }>(functions, "setUserAssignment");
        return (await call({ email, role, gemeindeId: activeGemeindeId, resendInvitation })).data;
    }

    async function handleInvite(e: React.FormEvent) {
        e.preventDefault();
        setBusy(true);
        try {
            const result = await assign(inviteEmail.trim(), inviteRole);
            setInviteEmail("");
            if (result?.created && !result.invited) notify("warning", t("superadmin.invitationFailed"));
            else notify("success", t(result?.created ? "members.invited" : "members.added"));
            await load();
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setBusy(false);
    }

    async function handleRoleChange(member: Member, role: Role) {
        setBusy(true);
        try {
            await assign(member.email, role);
            notify("success", t("members.roleChanged", { email: member.email }));
            await load();
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setBusy(false);
    }

    async function handleResend(member: Member) {
        setBusy(true);
        try {
            const result = await assign(member.email, (member.role as Role) ?? Globals.ROLE_MEMBER, true);
            notify(result?.invited ? "success" : "warning", t(result?.invited ? "superadmin.invitationSent" : "superadmin.invitationFailed"));
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setBusy(false);
    }

    async function handleRemove() {
        if (!toRemove) return;
        setBusy(true);
        try {
            const remove = httpsCallable(functions, "removeUserAssignment");
            await remove({ uid: toRemove.uid });
            notify("success", t("members.removed", { email: toRemove.email }));
            setToRemove(null);
            await load();
        } catch (err: any) {
            notify("danger", err?.message ?? t("errors.anErrorOccurred"));
        }
        setBusy(false);
    }

    const locale = t("calendar.locale") as string;

    return (
        <div className="mx-auto" style={{ maxWidth: "1100px" }}>
            <p className="text-muted">{t("members.intro")}</p>

            <Card className="mb-4">
                <Card.Body>
                    <Card.Title as="h5">{t("members.inviteTitle")}</Card.Title>
                    <Form onSubmit={handleInvite} className="d-flex flex-wrap gap-2 align-items-end">
                        <Form.Group controlId="members.inviteEmail" style={{ minWidth: "260px", flex: "1 1 260px" }}>
                            <Form.Label>{t("auth.email")}</Form.Label>
                            <Form.Control type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
                        </Form.Group>
                        <Form.Group controlId="members.inviteRole">
                            <Form.Label>{t("superadmin.role")}</Form.Label>
                            <Form.Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as Role)}>
                                <option value={Globals.ROLE_MEMBER}>{t("superadmin.roleMember")}</option>
                                <option value={Globals.ROLE_GEMEINDEADMIN}>{t("superadmin.roleGemeindeadmin")}</option>
                            </Form.Select>
                        </Form.Group>
                        <Button type="submit" disabled={busy}>
                            {t("members.invite")}
                        </Button>
                    </Form>
                    <Form.Text className="text-muted">{t("members.inviteHelp")}</Form.Text>
                </Card.Body>
            </Card>

            {loading ? (
                <Spinner animation="border" />
            ) : (
                <Table striped bordered hover responsive data-testid="gemeinde-members">
                    <thead>
                        <tr>
                            <th>{t("auth.email")}</th>
                            <th>{t("superadmin.role")}</th>
                            <th>{t("members.lastSignIn")}</th>
                            <th>{t("admin.actions")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {members.map((m) => {
                            const self = m.uid === user?.uid;
                            return (
                                <tr key={m.uid}>
                                    <td>
                                        {m.email}
                                        {self && <span className="text-muted"> ({t("members.you")})</span>}
                                    </td>
                                    <td style={{ minWidth: "190px" }}>
                                        <Form.Select
                                            size="sm"
                                            value={m.role ?? Globals.ROLE_MEMBER}
                                            disabled={busy || self}
                                            onChange={(e) => handleRoleChange(m, e.target.value as Role)}
                                            aria-label={t("superadmin.role") as string}
                                        >
                                            <option value={Globals.ROLE_MEMBER}>{t("superadmin.roleMember")}</option>
                                            <option value={Globals.ROLE_GEMEINDEADMIN}>{t("superadmin.roleGemeindeadmin")}</option>
                                        </Form.Select>
                                    </td>
                                    <td className="small">
                                        {m.lastSignIn ? new Date(m.lastSignIn).toLocaleDateString(locale) : t("members.neverSignedIn")}
                                    </td>
                                    <td>
                                        <div className="d-flex flex-wrap gap-1">
                                            <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => handleResend(m)}>
                                                {t("superadmin.resendInvitation")}
                                            </Button>
                                            {!self && (
                                                <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => setToRemove(m)}>
                                                    {t("members.remove")}
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            <Modal show={!!toRemove} onHide={() => setToRemove(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("members.removeTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{t("members.removeText", { email: toRemove?.email ?? "" })}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setToRemove(null)} disabled={busy}>
                        {t("common.cancel")}
                    </Button>
                    <Button variant="danger" onClick={handleRemove} disabled={busy}>
                        {t("members.remove")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
